// The Generate Case Sheet dialog — docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md
// §9.1, §10 Phase 7. Replaces PatientHeader.tsx's one-click DOCX button with
// a profile/purpose/recipient form, a Preview that renders the exact HTML
// the PDF export uses (inline, in an iframe — see handlePreview's own
// comment on why not a new tab), and a Generate that writes a real
// ip_case_sheet_exports row.
//
// DOCX is NOT offered here (see routes/ip/case-sheet.js's own header
// comment) — the pre-existing one-click "📄 Generate Case Sheet (.docx)"
// path stays available separately for whoever still wants the editable
// working copy; this dialog is PDF-only, matching §7.7's "DOCX is not the
// filed MRD copy when a PDF exists" framing.
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, NativeSelect } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';
import { usePrintProfiles, useGenerateCaseSheetExport } from './hooks';
import { fetchCaseSheetPreviewHtml, blockedDeficiencies } from './api';
import DocumentPreviewButtons from '@/components/DocumentPreviewButtons';
import type { CaseSheetDeficiencies, IpPrintProfile } from './types';

const RELEASE_PROFILES = new Set(['insurance_tpa', 'patient_copy']);

function DeficiencyList({ title, items, tone }: { title: string; items: { key: string; message: string }[]; tone: 'danger' | 'warn' }) {
  if (!items.length) return null;
  return (
    <div className="mt-2">
      <div className={`text-[12px] font-medium ${tone === 'danger' ? 'text-danger' : 'text-amber-700'}`}>
        {title} ({items.length})
      </div>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[12px] text-ink-muted">
        {items.map((d) => <li key={d.key}>{d.message}</li>)}
      </ul>
    </div>
  );
}

export function GenerateCaseSheetDialog({ admissionId, uhid, open, onOpenChange }: {
  admissionId: number;
  uhid: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user } = useAuth();
  const canRelease = !!user?.modules?.includes('ip:case-sheet-release');

  const { data: profiles, isLoading: profilesLoading } = usePrintProfiles();
  const visibleProfiles = useMemo(
    () => (profiles || []).filter((p) => canRelease || !RELEASE_PROFILES.has(p.code)),
    [profiles, canRelease]
  );

  const [profileCode, setProfileCode] = useState('interim');
  const [purpose, setPurpose] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientRelation, setRecipientRelation] = useState('');
  const [recipientOrg, setRecipientOrg] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [blocked, setBlocked] = useState<CaseSheetDeficiencies | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Rendered INLINE (an iframe inside this dialog), not a new window/tab —
  // found live that opening a tab from an async callback gets silently
  // eaten by the browser's own popup blocker even when the `window.open()`
  // call itself is moved earlier to stay gesture-synchronous, and
  // DocumentPreviewButtons.tsx already established the "preview inline in a
  // dialog" pattern everywhere else a document gets previewed in this app —
  // this follows that precedent rather than fighting popup blockers.
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const generate = useGenerateCaseSheetExport(admissionId);

  // Reset the form each time the dialog opens, rather than letting a
  // previous profile/purpose linger into a different generation attempt.
  useEffect(() => {
    if (!open) return;
    setProfileCode('interim');
    setPurpose(''); setRecipientName(''); setRecipientRelation(''); setRecipientOrg('');
    setOverrideReason(''); setBlocked(null); setPreviewError(null); setPreviewHtml(null);
    generate.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const profile: IpPrintProfile | undefined = visibleProfiles.find((p) => p.code === profileCode);

  const handlePreview = async () => {
    setPreviewLoading(true); setPreviewError(null);
    try {
      const html = await fetchCaseSheetPreviewHtml(admissionId, profileCode);
      setPreviewHtml(html);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Could not load the preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerate = () => {
    setBlocked(null);
    generate.mutate(
      {
        profile: profileCode,
        purpose: purpose || undefined,
        recipient: recipientName ? { name: recipientName, relation: recipientRelation || undefined, org: recipientOrg || undefined } : undefined,
        override_reason: overrideReason || undefined,
      },
      {
        onError: (e) => {
          const d = blockedDeficiencies(e);
          if (d) setBlocked(d);
        },
      }
    );
  };

  if (previewHtml !== null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[85vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle>Preview — {profile?.label || profileCode}</DialogTitle>
          </DialogHeader>
          <iframe
            title="Case sheet preview"
            srcDoc={previewHtml}
            className="min-h-0 flex-1 rounded-[var(--radius)] border border-border bg-white"
          />
          <DialogFooter className="mt-2">
            <Button variant="ghost" size="sm" onClick={() => setPreviewHtml(null)}>← Back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate case sheet</DialogTitle>
        </DialogHeader>

        {generate.isSuccess && generate.data ? (
          <div className="space-y-3">
            <div className="text-[13px] text-ink">
              ✅ Case sheet filed in Patient Records — {generate.data.page_count ?? '—'} page(s).
            </div>
            <DocumentPreviewButtons uhid={uhid} docId={generate.data.document_id} label="Case sheet" className="flex items-center gap-3" />
            {(generate.data.deficiencies.warnings.length > 0) && (
              <DeficiencyList title="Documentation gaps on file" items={generate.data.deficiencies.warnings} tone="warn" />
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-[12px] font-medium text-ink">Copy type</label>
              <NativeSelect
                value={profileCode}
                onChange={(e) => { setProfileCode(e.target.value); setBlocked(null); }}
                disabled={profilesLoading}
              >
                {visibleProfiles.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
              </NativeSelect>
              {!canRelease && (
                <p className="mt-1 text-[11px] text-ink-muted">Insurance/Patient copies need the case-sheet release permission.</p>
              )}
            </div>

            {profile?.require_reason && (
              <>
                <div>
                  <label className="mb-1 block text-[12px] font-medium text-ink">Purpose</label>
                  <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Medico-legal file closure" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-1">
                    <label className="mb-1 block text-[12px] font-medium text-ink">Recipient name</label>
                    <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <label className="mb-1 block text-[12px] font-medium text-ink">Relation</label>
                    <Input value={recipientRelation} onChange={(e) => setRecipientRelation(e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <label className="mb-1 block text-[12px] font-medium text-ink">Organisation</label>
                    <Input value={recipientOrg} onChange={(e) => setRecipientOrg(e.target.value)} />
                  </div>
                </div>
              </>
            )}

            {blocked && (
              <div className="rounded-[var(--radius)] border border-amber-300 bg-amber-50 p-3">
                <div className="text-[12px] font-medium text-amber-800">This record has blocking deficiencies for a Final copy.</div>
                <DeficiencyList title="Blocking" items={blocked.blocking} tone="danger" />
                {canRelease ? (
                  <div className="mt-2">
                    <label className="mb-1 block text-[12px] font-medium text-ink">Override reason (required to proceed)</label>
                    <Textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={2} />
                  </div>
                ) : (
                  <p className="mt-2 text-[12px] text-ink-muted">Overriding requires the case-sheet release permission — ask an Admin/MRD user.</p>
                )}
              </div>
            )}

            {generate.isError && !blocked && (
              <div className="text-[12px] text-danger">
                ⚠ {generate.error instanceof Error ? generate.error.message : 'Could not generate the case sheet.'}
              </div>
            )}
            {previewError && <div className="text-[12px] text-danger">⚠ {previewError}</div>}

            <Badge variant="neutral" className="w-fit">
              {profile?.watermark ? `Watermark: ${profile.watermark}` : 'No watermark'}
            </Badge>
          </div>
        )}

        <DialogFooter className="mt-2">
          {!generate.isSuccess && (
            <>
              <Button variant="ghost" size="sm" onClick={handlePreview} disabled={previewLoading}>
                {previewLoading ? 'Loading…' : '👁 Preview'}
              </Button>
              <Button
                size="sm"
                onClick={handleGenerate}
                disabled={generate.isPending || (!!blocked && canRelease && !overrideReason)}
              >
                {generate.isPending ? 'Generating…' : 'Generate'}
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {generate.isSuccess ? 'Close' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
